import React from 'react';
import { CircleCheckBig, Trash2 } from 'lucide-react';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { Task } from '@/types/tasks';
import TaskCard from '../TaskCard';
import { Button } from '@/components/ui/button';
import GlassPill from '@/components/ui/GlassPill';

interface CompletedTasksSectionProps {
  tasks: Task[];
  handleClearCompletedTasks: (e: React.MouseEvent) => void;
  customTaskOperations?: {
    completeTask?: (taskId: string) => void;
    uncompleteTask?: (taskId: string) => void;
    removeTask?: (taskId: string) => void;
    updateTask?: (taskId: string, updates: Partial<Task>, silent?: boolean) => void;
  };
}

const CompletedTasksSection: React.FC<CompletedTasksSectionProps> = ({
  tasks,
  handleClearCompletedTasks,
  customTaskOperations
}) => {
  // Set up droppable area for completed tasks
  const { setNodeRef: setCompletedTasksRef } = useDroppable({
    id: 'completed-tasks',
  });

  return (
    <div className="relative p-3 md:p-6 rounded-lg overflow-hidden">
      {/* Gradient background - matching card border radius */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-white/30 to-white/10 dark:from-black/40 dark:to-black/20 -z-10 rounded-lg" />
      
      {/* Top border highlight - matching card border radius */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-green-300/30 to-transparent dark:via-green-500/20 rounded-t-lg" />
      
      {/* Glass effect - matching card border radius */}
      <div className="absolute inset-0 backdrop-blur-xl backdrop-saturate-150 -z-10 rounded-lg" />
      
      <div className="flex items-center justify-between mb-3 md:mb-4">
        <div className="flex items-center gap-2">
          <CircleCheckBig className="text-green-500 dark:text-green-400" size={18} />
          <h3 className="font-medium text-base">Completed Tasks</h3>
          {tasks.length > 0 && (
            <GlassPill 
              type={tasks.length.toString()} 
              variant="green" 
              className="ml-2 text-xs h-6 w-6 flex items-center justify-center p-0"
            />
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Temporarily hidden - Clear all button */}
          {false && tasks.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="group h-8 px-3 text-muted-foreground bg-transparent hover:bg-green-500 hover:text-white dark:hover:bg-green-600 transition-all duration-200 rounded-full"
              onClick={handleClearCompletedTasks}
            >
              <Trash2 size={16} className="mr-1 group-hover:animate-pulse" />
              <span className="text-xs font-medium">Clear all</span>
            </Button>
          )}
        </div>
      </div>

      <div 
        ref={setCompletedTasksRef}
        className="pt-2 overflow-visible"
      >
        <SortableContext items={tasks.map(task => task.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {tasks.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center italic">
                No completed tasks yet.
              </p>
            ) : (
              tasks.map((task, index) => (
                <div key={task.id}>
                  <TaskCard key={task.id} task={task} context="task-manager" customTaskOperations={customTaskOperations} />
                </div>
              ))
            )}
          </div>
        </SortableContext>
      </div>
    </div>
  );
};

export default CompletedTasksSection;
